/* eslint-disable max-lines */
import PropTypes from 'prop-types';

import Loader from 'Component/Loader';
import Pagination from 'Component/Pagination';
import ProductListPage from 'Component/ProductListPage';
import {
    ProductList as SourceProductList
} from 'SourceComponent/ProductList/ProductList.component';
import { CategoryContentPushType } from 'Type/Category.type';
import { RefType } from 'Type/Common.type';
import { scrollToTop } from 'Util/Browser';

import './ProductList.override.style';

/** @namespace PeggysagePwa/Component/ProductList/Component */
export class ProductListComponent extends SourceProductList {
    static propTypes = {
        ...SourceProductList.propTypes,
        productListRef: RefType.isRequired,
        contentPushes: PropTypes.arrayOf(CategoryContentPushType),
        pageSize: PropTypes.number.isRequired,
        totalItems: PropTypes.number.isRequired,
        isPageLoading: PropTypes.bool.isRequired,
        onShowMoreButtonClick: PropTypes.func.isRequired,
        categoryId: PropTypes.number
    };

    static defaultProps = {
        contentPushes: [],
        categoryId: null
    };

    previousY = 0;

    previousRatio = 0;

    /**
     * Override: remove scroll to top with infinite loader
     */
    // eslint-disable-next-line @scandipwa/scandipwa-guidelines/only-render-in-component
    componentDidUpdate(prevProps) {
        const {
            isWidget, currentPage, device, isInfiniteLoaderEnabled
        } = this.props;
        const { currentPage: prevCurrentPage } = prevProps;

        // Scroll up on page change, ignore widgets
        if (prevCurrentPage !== currentPage && !isWidget && !device.isMobile && !isInfiniteLoaderEnabled) {
            scrollToTop({ behavior: 'smooth' });
        }

        if (isInfiniteLoaderEnabled) {
            this.observePageChange();
        }
    }

    /**
     * Override: modify page change detection since observer is no longer on whole container but only on first product
     */
    // eslint-disable-next-line @scandipwa/scandipwa-guidelines/only-render-in-component
    observePageChange() {
        const { updatePage, isLoading } = this.props;

        if (isLoading) {
            this.pagesIntersecting = [];
        }

        if (!this.observer && 'IntersectionObserver' in window) {
            const threshold = this._getThreshold();

            this.observer = new IntersectionObserver((entries) => {
                const { currentPage } = this.props;

                entries.forEach((entry) => {
                    const {
                        target,
                        isIntersecting,
                        intersectionRatio: currentRatio,
                        boundingClientRect: { y: currentY }
                    } = entry;

                    const page = +Object.keys(this.nodes).find((node) => this.nodes[node] === target.parentNode);

                    // Scrolling down/up
                    if (currentY < this.previousY) {
                        if (currentRatio > this.previousRatio && isIntersecting) {
                            // Scrolling down enter
                            if (page !== currentPage) {
                                updatePage(page);
                            }
                        } else {
                            // Scrolling down leave
                            // eslint-disable-next-line no-lonely-if
                            if (page !== currentPage) {
                                // Bugfix: first product appears above the middle of the viewport,
                                // change of page is not detected...)
                                updatePage(page);
                            }
                        }
                    } else if (currentY > this.previousY && isIntersecting) {
                        if (currentRatio < this.previousRatio) {
                            // Scrolling up leave
                            const pagesNumbers = Object.keys(this.nodes).map((key) => +key);
                            const index = pagesNumbers.indexOf(page);
                            if (index > 0) {
                                const prevPage = pagesNumbers[index - 1];
                                if (prevPage !== currentPage) {
                                    updatePage(prevPage);
                                }
                            }
                        } else {
                            // Scrolling up enter
                        }
                    }

                    this.previousY = currentY;
                    this.previousRatio = currentRatio;
                });
            }, {
                rootMargin: '0px 0px -400px 0px',
                threshold
            });
        }

        this.updateObserver();
    }

    /**
     * Override: observe first products in lists instead of containers since containers are no longer in flux
     * because of "display: contents" in CSS
     */
    // eslint-disable-next-line @scandipwa/scandipwa-guidelines/only-render-in-component
    updateObserver() {
        const currentNodes = Object.values(this.nodes);

        if (!this.observer || currentNodes.length <= 0) {
            return;
        }

        const newCurrentNodes = currentNodes.map((parentNode) => {
            if (parentNode && parentNode.children.length) {
                // Exclude pushes since they can be hidden by CSS
                const firstElement = Object.values(parentNode.children).find(
                    (node) => !node.classList.contains('ProductListContentPush')
                );

                if (firstElement) {
                    return firstElement;
                }
            }

            return parentNode;
        });

        newCurrentNodes.forEach((node) => {
            if (node && !this.observedNodes.includes(node)) {
                this.observer.observe(node);
                this.observedNodes.push(node);
            }
        });

        this.observedNodes = this.observedNodes.reduce((acc, node) => {
            if (!newCurrentNodes.includes(node)) {
                this.observer.unobserve(node);
            } else {
                acc.push(node);
            }

            return acc;
        }, []);
    }

    /**
     * Override: real button
     * @returns {JSX.Element|null}
     */
    renderLoadButton() {
        const {
            isShowLoading,
            isInfiniteLoaderEnabled,
            loadPrevPage,
            isPageLoading
        } = this.props;

        if (!isShowLoading || !isInfiniteLoaderEnabled) {
            return null;
        }

        return (
            <div block="ProductList" elem="LoadButtonWrapper">
                <button
                  type="button"
                  block="Button"
                  mods={ { isHollow: true } }
                  mix={ { block: 'ProductList', elem: 'LoadButton' } }
                  onClick={ loadPrevPage }
                  disabled={ isPageLoading }
                >
                    { __('Load previous products') }
                </button>
            </div>
        );
    }

    /**
     * Override: no message for widgets
     * @returns {JSX.Element}
     */
    renderNoProducts() {
        const { isWidget } = this.props;

        if (isWidget) {
            return null;
        }

        return super.renderNoProducts();
    }

    /**
     * Override: remove placeholders
     * @returns {JSX.Element|unknown[]}
     */
    renderPages() {
        const {
            pages,
            // isVisible,
            isLoading
            // isInfiniteLoaderEnabled
        } = this.props;

        if (isLoading) {
            return this.renderPage();
        }

        const pageRenders = Object.entries(pages).map(this.renderProductPage.bind(this));

        /* if (isVisible && isInfiniteLoaderEnabled) { // add placeholders to the end of pages if needed
            const key = Math.max(Object.keys(pages)) + 1; // the key should match next page key

            pageRenders.push(this.renderPage({ key }));
        } */

        return pageRenders;
    }

    /**
     * Override: pass content pushes
     * @param props
     * @returns {JSX.Element}
     */
    renderPage(props = {}) {
        const {
            isInfiniteLoaderEnabled,
            loadPage,
            isLoading,
            isVisible,
            mix,
            isPlp,
            contentPushes,
            isWidget,
            categoryId
        } = this.props;
        const {
            items,
            keys,
            pageNumber,
            selectedFilters,
            wrapperRef,
            key
        } = this._processProps(props);

        return (
            <ProductListPage
              key={ key }
              isInfiniteLoaderEnabled={ isInfiniteLoaderEnabled }
              updatePages={ loadPage }
              isLoading={ isLoading }
              isVisible={ isVisible }
              ix={ mix }
              items={ items }
              keys={ keys }
              pageNumber={ pageNumber }
              selectedFilters={ selectedFilters }
              wrapperRef={ wrapperRef }
              isPlp={ isPlp }
              // pushes are hidden in CSS  (everywhere except in the first list)
              contentPushes={ /* +key < 2 ? contentPushes : [] */contentPushes }
              isWidget={ isWidget }
              categoryId={ categoryId }
            />
        );
    }

    /**
     * Override: add parameters
     * @returns {JSX.Element|null}
     */
    renderPagination() {
        const {
            isLoading,
            totalPages,
            isPaginationEnabled,
            pages,
            pageSize,
            totalItems,
            isPageLoading,
            onShowMoreButtonClick
        } = this.props;

        if (!isPaginationEnabled) {
            return null;
        }

        const pagesCount = Object.keys(pages).length;

        return (
            <Pagination
              isLoading={ isLoading || isPageLoading }
              totalPages={ totalPages }
              pageSize={ pageSize }
              pagesCount={ pagesCount }
              totalItems={ totalItems }
              onShowMoreButtonClick={ onShowMoreButtonClick }
            />
        );
    }

    /**
     * Override: add reference
     * @returns {JSX.Element|*}
     */
    render() {
        const {
            totalPages,
            isLoading,
            mix,
            productListRef
        } = this.props;

        if (!isLoading && totalPages === 0) {
            return this.renderNoProducts();
        }

        if (isLoading) {
            return (
                <Loader />
            );
        }

        return (
            <div
              block="ProductList"
              mods={ { isLoading } }
              mix={ mix }
              ref={ productListRef }
            >
                { this.renderTitle() }
                { this.renderLoadButton() }
                { this.renderPages() }
                { this.renderPagination() }
            </div>
        );
    }
}

export default ProductListComponent;
